import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { Table, TableBody, TableHead, Tooltip } from '@mui/material';
import { StyledTableCell, StyledTableRow } from '../StyledTable';
import { humanize } from '../util/StringUtil';
import { displayDate, displayTimeWithTz } from '../util/TimeUtil';
import { groupBy } from '../util/ObjectUtil';
import Divider from '@mui/material/Divider';
import { SectionTitle, SubText } from './StyledComponent';
import MUIDataTable from 'mui-datatables';
import dayjs from 'dayjs';
export default function ManageUsersTable(props) {
    const { users } = props;
    const firms = [
        ...users
            .filter(f => f.firm != null)
            .reduce((a, c) => {
            if (c.firm) {
                a.set(c.firm.id || -1, c.firm);
            }
            return a;
        }, new Map())
            .values(),
    ].sort((a, b) => b.creationDate.localeCompare(a.creationDate));
    const usersPerFirm = groupBy(users, user => { var _a; return ((_a = user.firm) === null || _a === void 0 ? void 0 : _a.id) || -1; });
    const columns = [
        {
            name: 'name',
            label: 'Firm',
            options: {
                filter: false,
                sort: false,
                customBodyRenderLite: (dataIndex) => {
                    const firm = firms[dataIndex];
                    return (_jsxs("div", { children: [firm.name, " ", _jsxs("i", { children: ["(id: ", firm.id, ")"] })] }));
                },
            },
        },
        {
            name: 'firm.billingUser',
            label: 'Billing contact',
            options: {
                filter: false,
                sort: false,
                customBodyRenderLite: (dataIndex) => {
                    const firm = firms[dataIndex];
                    return (_jsxs("div", { children: [_jsx("i", { children: firm.billingUser.email }), _jsxs(SubText, { children: [firm.billingUser.givenName, " ", firm.billingUser.familyName] })] }));
                },
            },
        },
        {
            name: 'subscription.level',
            label: 'Subscription Level',
            options: {
                filter: true,
                sort: true,
                customBodyRender(value) {
                    return humanize(value);
                },
            },
        },
        {
            name: 'subscription.status',
            label: 'Subscription Status',
            options: {
                filter: true,
                sort: true,
                customBodyRenderLite: (dataIndex) => {
                    const firm = firms[dataIndex];
                    const trialDate = firm.subscription.status === 'TRIAL' ? ` (until ${displayDate(firm.subscription.trialEndDate)})` : '';
                    return humanize(firm.subscription.status) + trialDate;
                },
            },
        },
        {
            name: 'connectionCount',
            label: 'Connections count (firm)',
            options: {
                filter: false,
                sort: true,
                customBodyRenderLite: (dataIndex) => {
                    const firm = firms[dataIndex];
                    return (_jsxs("div", { children: [firm.connectionCount, " / ", firm.subscription.connectionLimit] }));
                },
            },
        },
        {
            name: 'firm.connectionCount',
            label: '# Users',
            options: {
                filter: false,
                sort: false,
                customBodyRenderLite: (dataIndex) => {
                    const firm = firms[dataIndex];
                    return (_jsx(Tooltip, { title: _jsx("div", { children: usersPerFirm[firm.id].map((user) => {
                                return (_jsxs("div", { children: [user.givenName, " ", user.familyName, " ", user.xeroUserUuid] }));
                            }) }), children: _jsx("div", { children: usersPerFirm[firm.id].length }) }));
                },
            },
        },
        {
            name: 'creationDate',
            label: 'Creation date',
            options: {
                filter: false,
                sort: true,
                customBodyRender(value) {
                    return displayTimeWithTz(value);
                },
            },
        },
        {
            name: 'updateDate',
            label: 'Last updated',
            options: {
                filter: false,
                sort: true,
                customBodyRenderLite: (dataIndex) => {
                    const firm = firms[dataIndex];
                    return displayTimeWithTz(dayjs.max(dayjs.utc(firm.updateDate), dayjs.utc(firm.subscription.updateDate)));
                },
            },
        },
    ];
    const options = {
        setRowProps: (row, dataIndex) => {
            const firm = firms[dataIndex];
            if (firm.subscription.status === 'ACTIVE') {
                return {
                    style: { backgroundColor: 'lightgreen' },
                };
            }
            return {};
        },
        enableNestedDataAccess: '.',
        rowsPerPageOptions: [25, 50, 100],
        print: false,
        download: false,
        viewColumns: false,
        storageKey: 'manageUserConfig',
        selectableRowsHideCheckboxes: true,
        fixedHeader: true,
        setTableProps: () => {
            return {
                // material ui v5 only
                size: 'small',
            };
        },
    };
    const countPerSubscription = (subscriptionLevel) => {
        return firms.filter(firm => firm.subscription.level.toLowerCase() == subscriptionLevel.toLowerCase()).length;
    };
    const countTrials = () => {
        return firms.filter(firm => firm.subscription.status == 'TRIAL').length;
    };
    const title = (_jsxs(_Fragment, { children: [_jsx(SectionTitle, { children: "Firms" }), _jsxs(SubText, { style: { display: 'flex', gap: '8px' }, children: [_jsx("span", { children: "Totals:" }), _jsxs("span", { children: ["Starter: ", countPerSubscription('starter'), " (", countTrials(), " trials)"] }), _jsx("span", { children: "-" }), _jsxs("span", { children: ["Intermediate: ", countPerSubscription('intermediate')] }), _jsx("span", { children: "-" }), _jsxs("span", { children: ["Business: ", countPerSubscription('business')] }), _jsx("span", { children: "-" }), _jsxs("span", { children: ["Business plus: ", countPerSubscription('business_plus')] }), _jsx("span", { children: "-" }), _jsxs("span", { children: ["Total connections: ", firms.map(firm => firm.connectionCount).reduce((a, b) => a + b, 0)] }), _jsx("span", { children: "-" }), _jsxs("span", { children: ["Total users: ", users.length] })] })] }));
    return (_jsxs(_Fragment, { children: [_jsx(MUIDataTable, { title: title, data: firms || [], columns: columns, options: options }), usersPerFirm[-1] && (_jsxs(_Fragment, { children: [_jsx(Divider, { sx: { mt: 5, mb: 2 }, textAlign: "left", children: "Users without firm" }), _jsxs(Table, { size: "small", children: [_jsx(TableHead, { children: _jsxs(StyledTableRow, { children: [_jsx(StyledTableCell, { align: "center", children: "Xero User Id" }), _jsx(StyledTableCell, { align: "center", children: "Name" }), _jsx(StyledTableCell, { align: "center", children: "Email" }), _jsx(StyledTableCell, { align: "center", children: "Creation Date" })] }) }), _jsx(TableBody, { children: usersPerFirm[-1].map((user, index) => (_jsxs(StyledTableRow, { children: [_jsx(StyledTableCell, { align: "left", children: user.xeroUserUuid }), _jsxs(StyledTableCell, { align: "left", children: [user.familyName, " ", user.givenName] }), _jsxs(StyledTableCell, { align: "left", children: [user.email, " ", _jsxs("i", { children: ["(id: ", user.id, ")"] })] }), _jsxs(StyledTableCell, { align: "center", children: ["Created ", user.creationDate] })] }, `user{index}`))) })] })] })), _jsx("br", {}), _jsx("div", { children: "All users with emails" }), _jsx("textarea", { children: users.map((user) => {
                    return user.givenName + "," + user.email;
                }).join("\n") })] }));
}
